/** @format */

import { defineStore } from 'pinia'
import ClientFilesService from '@/api/ClientFilesService'
import ClientService from '@/api/ClientService'

export const useClientFilesStore = defineStore('clientFiles', {
	state: () => ({
		clientId: null,
		sections: {},
		loading: false,
		loaded: false,
		loadFailed: false,
		deleting: false,
		savingYearEnd: false,
	}),

	getters: {
		corporateDocuments(state) {
			return state.sections.corporate_documents ?? {}
		},

		financials(state) {
			return state.sections.financials ?? { year_end: null, days_in_month: [], years: [] }
		},

		creditAnalysis(state) {
			const section = state.sections.credit_analysis ?? {}
			return { dnb_report: section.dnb_report ?? [], api_reports: section.api_reports ?? null }
		},
	},

	actions: {
		notification(severity, detail) {
			const summary = severity === 'error' ? 'Error' : 'Success'
			this.toast.add({
				severity,
				summary,
				detail,
				life: this.getToastDuration(detail, severity),
			})
		},

		_sectionOf(type) {
			const key = Object.keys(this.sections).find(
				(section) =>
					section !== 'financials' &&
					Object.prototype.hasOwnProperty.call(this.sections[section] ?? {}, type),
			)
			return key ?? null
		},

		_yearOf(year) {
			return this.financials.years.find((entry) => entry.year === year) ?? null
		},

		async fetchFiles(clientId) {
			this.clientId = clientId
			this.loaded = false
			this.loadFailed = false
			this.loading = true
			try {
				const { data } = await ClientFilesService.fetch(clientId)
				if (this.clientId !== clientId) return
				this.sections = data?.sections ?? {}
				this.loaded = true
			} catch (error) {
				if (this.clientId !== clientId) return
				console.error('Error fetching company files (stores/clientFiles/fetchFiles):', error)
				this.loadFailed = true
			} finally {
				if (this.clientId === clientId) this.loading = false
			}
		},

		addFile(file, clientId) {
			if (!file || clientId !== this.clientId) return

			if (file.fiscal_year) {
				const entry = this._yearOf(file.fiscal_year)
				if (!entry || !entry.types[file.type]) return

				entry.types[file.type] = [file, ...entry.types[file.type]]
				return
			}

			const section = this._sectionOf(file.type)
			if (!section) return

			this.sections[section][file.type] = [file, ...(this.sections[section][file.type] ?? [])]
		},

		async download(fileId, fileName) {
			try {
				await ClientFilesService.download(this.clientId, fileId, fileName)
			} catch (error) {
				console.error('Error downloading company file (stores/clientFiles/download):', error)
			}
		},

		async getPreviewUrl(fileId) {
			try {
				const { data } = await ClientFilesService.preview(this.clientId, fileId)
				return data.url
			} catch (error) {
				console.error(
					'Error fetching company file preview URL (stores/clientFiles/getPreviewUrl):',
					error,
				)
				this.notification('error', '§ui_message.preview_failed')
				return null
			}
		},

		async downloadApiReport(reportId, fileName) {
			try {
				await ClientFilesService.downloadApiReport(this.clientId, reportId, fileName)
			} catch (error) {
				console.error(
					'Error downloading DnB API report (stores/clientFiles/downloadApiReport):',
					error,
				)
			}
		},

		async getApiReportJson(reportId) {
			try {
				const { data } = await ClientService.fetchStandardDataByReport({
					client: this.clientId,
					dnb_report: reportId,
				})
				return data.dnb_report_json
			} catch (error) {
				console.error(
					'Error fetching DnB API report (stores/clientFiles/getApiReportJson):',
					error,
				)
				this.notification('error', '§ui_message.preview_failed')
				return null
			}
		},

		async delete({ file, type, year }) {
			this.deleting = true
			try {
				await ClientFilesService.delete(this.clientId, file)
				if (year) {
					this._removeFromYear(file, type, year)
				} else {
					const section = this._sectionOf(type)
					if (section) {
						this.sections[section][type] = this.sections[section][type].filter(
							(f) => f.id !== file,
						)
					}
				}
				this.notification('success', '§ui_message.delete_successful')
			} catch (error) {
				console.error('Error deleting company file (stores/clientFiles/delete):', error)
				this.notification('error', '§ui_message.delete_failed')
			} finally {
				this.deleting = false
			}
		},

		_removeFromYear(fileId, type, year) {
			const entry = this._yearOf(year)
			if (!entry || !entry.types[type]) return

			entry.types[type] = entry.types[type].filter((f) => f.id !== fileId)

			const empty = Object.values(entry.types).every((files) => files.length === 0)
			if (!entry.in_window && empty) {
				this.sections.financials.years = this.sections.financials.years.filter(
					(e) => e.year !== year,
				)
			}
		},

		async saveYearEnd(value) {
			const clientId = this.clientId
			this.savingYearEnd = true
			try {
				const { data } = await ClientFilesService.updateYearEnd(clientId, value)
				if (this.clientId !== clientId) return false
				this.sections.financials = data.financials
				this.notification('success', '§ui_message.update_successful')
				return true
			} catch (error) {
				if (this.clientId !== clientId) return false
				console.error(
					'Error saving the financial year end (stores/clientFiles/saveYearEnd):',
					error,
				)
				this.notification('error', '§ui_message.update_failed')
				return false
			} finally {
				if (this.clientId === clientId) this.savingYearEnd = false
			}
		},

		resetState() {
			this.clientId = null
			this.sections = {}
			this.loading = false
			this.loaded = false
			this.loadFailed = false
			this.savingYearEnd = false
		},
	},
})
